import { deserify } from '@geneo2-web/shared-ui';
import { useEffect, useRef } from 'react';
import { useLiveQuizContext } from '../../../app/Context/LiveQuizContextProvider';
import { useAppDispatch, useAppSelector } from '../../../reduxStore/reduxHooks';
import {
  HOME,
  HOMEWORK_ONGOING,
  LIVE_QUIZ_LOBBY,
  LIVE_QUIZ_PLAY,
  LIVE_QUIZ_START,
} from '../../../routeHandling/RoutesNomenclature';
import { useGlobalContext } from '../../../app/Context/GlobalContextProvider';
import { useNavigate } from 'react-router-dom';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';

// HOMEWORK_ONGOING stands in for the old dedicated Manage route — Live Quiz
// mode now reuses that same shared route (OngoingHomework special-cases
// MODE_OF_CONDUCTION_WEB_LIVE inline) rather than routing to its own page.
// This can't distinguish "back to this same live quiz's Manage view" from
// "navigated to a different assessment's ongoing-homework page" by path
// alone (the assessment id lives in Redux, not the URL) — an accepted,
// narrow imprecision from sharing the route, matching how OMR mode already
// shares it with no such distinction either.
const LIVE_QUIZ_ROUTES: string[] = [
  LIVE_QUIZ_START,
  HOMEWORK_ONGOING,
  LIVE_QUIZ_LOBBY,
  LIVE_QUIZ_PLAY,
];

// Called from the Lobby and Play screens — the two places a session is
// genuinely "live" (STARTED). Navigating away without explicitly ending the
// session (closing the tab, going Back to Home, clicking into a different
// assessment) leaves it paused rather than silently still running with no
// teacher watching; createLiveQuizSession transparently resumes a paused
// session for the same (teacher, assessment, section) rather than forking a
// second one, so coming back and clicking START again just continues it.
// window.location.pathname (not the reactive `location` object) is read
// inside the cleanup deliberately — by the time this fires, React Router
// has already committed the navigation, so the closure's own `location`
// would still be the page being left, not the destination.
export const usePauseLiveQuizOnLeave = () => {
  const {
    pauseLiveQuizSession,
    resetLiveQuizData,
    fetchRoster,
    hasSessionInstance,
    detachLiveQuizTab,
  } = useLiveQuizContext();
  const { live_quiz_session } = deserify(
    useAppSelector((state) => state.liveQuiz)
  );
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const sessionRef = useRef(live_quiz_session);
  const teacherIdRef = useRef(user_info?.teacherProfileId);
  sessionRef.current = live_quiz_session;
  teacherIdRef.current = user_info?.teacherProfileId;
  const { setSelectedFunction, setSelectedLogoClickHandler } =
    useGlobalContext();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  // const { questionSessionStatus } = useLiveQuizContext();

  // useEffect(() => {
  //   console.log('questionSessionStatus', questionSessionStatus);
  //   if (questionSessionStatus === 'error') {
  //     navigate(HOME, { replace: true });
  //     resetLiveQuiz();
  //     dispatch(
  //       setToastInfo({
  //         label: 'Something went wrong .',
  //         variant: 'error',
  //         open: true,
  //       })
  //     );
  //   }
  //   if (questionSessionStatus === 'stale') {
  //     // This tab's session is stale — another tab advanced the quiz.
  //     navigate(HOME, { replace: true });
  //     resetLiveQuiz();
  //     dispatch(
  //       setToastInfo({
  //         label: 'This live quiz session has been updated in another tab.',
  //         variant: 'error',
  //         open: true,
  //       })
  //     );
  //   }
  // }, [questionSessionStatus]);

  useEffect(() => {
    if (live_quiz_session?.liveQuizSessionId && user_info?.teacherProfileId) {
      fetchRoster(
        live_quiz_session.liveQuizSessionId,
        user_info.teacherProfileId
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live_quiz_session?.liveQuizSessionId]);
  useEffect(() => {
    // return () => {
    //   const stillOnLiveQuizRoute = LIVE_QUIZ_ROUTES.some((route) =>
    //     window.location.pathname.startsWith(route)
    //   );
    //   const session = sessionRef.current;
    //   const teacherId = teacherIdRef.current;
    //   if (!stillOnLiveQuizRoute && session?.liveQuizSessionId && teacherId) {
    //     pauseLiveQuizSession(session.liveQuizSessionId, teacherId);
    //   }
    // };
    const pauseIfLive = async () => {
      const session = sessionRef.current;
      const teacherId = teacherIdRef.current;
      if (session?.liveQuizSessionId && teacherId) {
        await pauseLiveQuizSession(session.liveQuizSessionId, teacherId);
      }
    };

    const leave = async (to: string) => {
      if (hasSessionInstance()) {
        await pauseIfLive();
        resetLiveQuizData(); // this tab owns the session
      } else {
        detachLiveQuizTab(); // stale tab: don't touch shared state
      }
      navigate(to, { replace: true });
    };

    // back: GlobalContextProvider's popstate listener runs this
    // AFTER the browser has already navigated,
    setSelectedFunction(() => () => {
      // const stillOnLiveQuizRoute = LIVE_QUIZ_ROUTES.some((route) =>
      //   window.location.pathname.startsWith(route)
      // );
      leave(HOMEWORK_ONGOING);
    });
    setSelectedLogoClickHandler(() => () => {
      leave(HOME);
    });

    return () => {
      setSelectedFunction(null);
      setSelectedLogoClickHandler(null);
    };
  }, []);
};
